import { motion } from "framer-motion";
import { ArrowRight, Building2, Globe, Search, Users } from "lucide-react";

const CARDS = [
  {
    id: "workspace",
    label: "Inside your workspace",
    labelIcon: Building2,
    artIcon: Users,
    title: "For you and your team.",
    body: "Find what you need across your documents, research, and company knowledge. Search directly, or bring the right context into your AI assistant.",
    uses: ["Personal research", "Team knowledge", "AI assistants"],
    cta: { label: "Start searching", href: "https://app.wondersearch.ai/" },
  },
  {
    id: "product",
    label: "Inside your product",
    labelIcon: Globe,
    artIcon: Search,
    title: "For the people you serve.",
    body: "Build WonderSearch into your app, help center, or AI experience. Let your users find what they need, through the product you create.",
    uses: ["In-app search", "Self-service support", "AI features"],
    cta: {
      label: "Let’s talk about your product",
      href: "https://cal.com/wondersearch/15min",
    },
  },
];

export const DualAudience = () => (
  <section
    id="use-cases"
    data-testid="dual-audience"
    className="mx-auto max-w-7xl px-6 py-24 md:px-12 md:py-32"
  >
    <motion.div
      initial={{ opacity: 0, y: 24 }}
      whileInView={{ opacity: 1, y: 0 }}
      viewport={{ once: true, margin: "-80px" }}
      transition={{ duration: 0.7, ease: [0.22, 1, 0.36, 1] }}
    >
      <span className="eyebrow">One search. Both sides of your business.</span>
      <h2 className="mt-4 text-3xl font-medium tracking-tight text-[#f3f5f3] md:text-4xl lg:text-5xl">
        Use it yourself.
        <br />
        <span className="text-[#889296]">Build it into your product.</span>
      </h2>
    </motion.div>

    <div className="mt-14 grid grid-cols-1 gap-6 md:grid-cols-2 md:gap-8">
      {CARDS.map((card, i) => (
        <motion.article
          key={card.id}
          initial={{ opacity: 0, y: 24 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true, margin: "-60px" }}
          transition={{ duration: 0.6, delay: i * 0.12, ease: [0.22, 1, 0.36, 1] }}
          data-testid={`audience-card-${card.id}`}
          className="group rounded-3xl border border-[#233c4d]/30 bg-[#111813] p-8 transition-colors duration-300 hover:border-[#233c4d]/70 md:p-10"
        >
          <div className="flex items-center justify-center rounded-2xl border border-[#233c4d]/40 bg-[#10191f] py-10">
            <card.artIcon
              className="h-10 w-10 text-[#bddcec] transition-transform duration-300 group-hover:scale-110"
              strokeWidth={1.5}
              aria-hidden="true"
            />
          </div>
          <span className="eyebrow mt-8 flex items-center gap-2">
            <card.labelIcon className="h-3.5 w-3.5" aria-hidden="true" />
            {card.label}
          </span>
          <h3 className="mt-3 text-xl font-medium text-[#f3f5f3] md:text-2xl">
            {card.title}
          </h3>
          <p className="mt-3 text-sm leading-relaxed text-[#889296] md:text-base">
            {card.body}
          </p>
          <ul className="mt-5 flex flex-wrap gap-2">
            {card.uses.map((use) => (
              <li
                key={use}
                className="rounded-full border border-[#233c4d]/40 bg-[#0b100d] px-3 py-1 text-xs text-[#c3cbce]"
              >
                {use}
              </li>
            ))}
          </ul>
          <a
            href={card.cta.href}
            target="_blank"
            rel="noopener noreferrer"
            data-testid={`audience-cta-${card.id}`}
            className="group/link mt-7 inline-flex items-center gap-2 text-sm font-semibold text-[#edf2f4] transition-colors duration-200 hover:text-[#bce8d1]"
          >
            {card.cta.label}
            <ArrowRight
              className="h-4 w-4 transition-transform duration-200 group-hover/link:translate-x-1"
              aria-hidden="true"
            />
          </a>
        </motion.article>
      ))}
    </div>
  </section>
);
